import React, { useEffect, useState } from 'react';
import {
    Box,
    Grid,
    OutlinedInput,
    Paper,
    Typography,
    Stack,
    Divider,
    Chip,
    Tooltip,
    TextField,
    IconButton,
    Button,
    InputLabel,
    FormControl,
    RadioGroup,
    FormLabel,
    FormControlLabel,
    Radio,
} from '@mui/material';
import InfoIcon from '@mui/icons-material/Info';
import EditIcon from '@mui/icons-material/Edit';
import CancelIcon from '@mui/icons-material/Cancel';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogActions from '@mui/material/DialogActions';
import { useSelector } from 'react-redux';
import { useAppDispatch } from '../../../store/store';

import { isValid } from 'date-fns';
import { toDate, formatDate } from '../../../utils/dateHelpers';
import { setShowMessage } from '../../../store/shared.slice';
import AddPlanModal from '../AddPlanModal';
import TextMaskCustom from '../../Shared/PhoneMask';
import { PlanItem } from '../../../Models/CarePlans/PlanItem.model';
import { copyObject } from '../../../utils/common';
import { RootState } from '../../../reducers';
import { PlanItemTypeEnum } from '../../../Enum/PlanItemTypeEnum';
import { PlanStatusEnum } from '../../../Enum/PlanStatusEnum';
import { CarePlanTypeEnum } from '../../../Enum/CarePlanTypeEnum';
import {
    setModalTitle,
    setModalState,
    setEditId,
    setFieldValues,
    setConfirmOpen,
    setConfirmItem,
    setConfirmType,
    setCareCircleConfirmOpen,
    setPendingCareCircleItem,
    setPendingCareCircleSection,
    saveSafetyPlan,
    fetchSafetyPlanByMemberId,
    getDefaultCareCircle,
    saveCareCircleItem,
    savePlanPDFToDocuments,
} from '../../../store/behaviorOrSafetyPlanSlice.slice';
import Authorization from '../../../utils/Authorization';
import { useParams } from 'react-router-dom';

const SECTION_CONFIG = [
    {
        key: 'Triggers',
        sectionLabel: 'Triggers',
        title: 'Step 1: What are the triggers and stressors (behaviors, situations, circumstances) that put you at emotional risk?',
        addLabel: 'Add Trigger',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'WarningSigns',
        sectionLabel: 'Warning Signs',
        title: 'Step 2: What are the warning signs (thoughts, images, mood, situation, behavior) that a crisis may be developing?',
        addLabel: 'Add Warning Sign',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'Strategies',
        sectionLabel: 'Coping Strategies',
        title: 'Step 3: Internal coping strategies: Things I can do to take my mind off my problems without contacting another person (relaxation techniques, physical activity, hobbies, etc.).',
        addLabel: 'Add Strategy',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'PositiveDistractions',
        sectionLabel: 'Positive Distractions',
        title: 'Step 4: Who are some people/what are some social settings that provide me with positive distractions (family, friends, AA/NA/GA, etc.)?',
        addLabel: 'Add Positive Distraction',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'People',
        sectionLabel: 'Helpful People',
        title: 'Step 5: People whom I can ask for help:',
        addLabel: 'Add Person',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'Agencies',
        sectionLabel: 'Professionals/Agencies',
        title: 'Step 6: Professionals or Agencies I can contact during a crisis:',
        addLabel: 'Add Professional/Agency',
        fields: [
            { name: 'Type', label: 'Type' },
            { name: 'Name', label: 'Name' },
            { name: 'Phone', label: 'Phone', mask: true },
            { name: 'Note', label: 'Note', multiline: true },
        ],
    },
    {
        key: 'Environments',
        sectionLabel: 'Environment Safety',
        title: 'Step 7: Making the environment safe:',
        addLabel: 'Add Environment',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
    {
        key: 'Avoids',
        sectionLabel: 'Avoids',
        title: 'Step 8: I should avoid the following people, places, or things:',
        addLabel: 'Add Avoid',
        fields: [{ name: 'Note', label: 'Note', multiline: true }],
    },
];

function getInitialFieldValues(fields) {
    const obj = {};
    fields.forEach((f) => {
        obj[f.name] = '';
    });
    return obj;
}

const Section = ({ sectionLabel, title, items, onAdd, renderItem, addLabel, gridProps = null }) => (
    <Grid size={{ xs: 12, md: 6 }} {...gridProps}>
        <Paper elevation={2} sx={{ p: 2, mb: 3 }}>
            <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                {sectionLabel}
            </Typography>
            <Stack direction="row" justifyContent="space-between" alignItems="center" mb={1}>
                <Typography variant="body1" fontWeight="bold">
                    {title}
                </Typography>
                <Tooltip title={addLabel}>
                    <IconButton onClick={onAdd} sx={{ ml: 2 }} color="primary">
                        <AddCircleOutlineIcon fontSize="large" />
                    </IconButton>
                </Tooltip>
            </Stack>
            <Divider sx={{ mb: 2 }} />
            {items?.length === 0 ? (
                <Typography color="text.secondary" fontStyle="italic">
                    No entries yet.
                </Typography>
            ) : (
                <Stack spacing={2}>{items.map(renderItem)}</Stack>
            )}
        </Paper>
    </Grid>
);

const ItemCard = ({ note, createdAt, createdBy, updatedAt, updatedBy, onEdit, onDelete, extraFields = null }) => (
    <Paper variant="outlined" sx={{ p: 2 }}>
        <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
            <Box sx={{ flex: 1 }}>
                <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                    {note}
                </Typography>
                {extraFields}
                <Stack spacing={1} mt={2}>
                    <Typography variant="caption" color="text.secondary">
                        Updated: {updatedAt ? formatDate(updatedAt, 'MM/dd/yyyy hh:mm a') : '-'} {updatedBy?.Name && `by ${updatedBy.Name}`}
                    </Typography>
                </Stack>
            </Box>
            <Stack direction="row" spacing={1}>
                <Tooltip title="Edit">
                    <IconButton onClick={onEdit}>
                        <EditIcon color="primary" />
                    </IconButton>
                </Tooltip>
                <Tooltip title="Delete">
                    <IconButton onClick={onDelete}>
                        <CancelIcon color="error" />
                    </IconButton>
                </Tooltip>
            </Stack>
        </Stack>
    </Paper>
);

const SafetyPlan = () => {
    const dispatch = useAppDispatch();
    const params = useParams();
    const memberId = params.memberId || '';
    const {
        safetyPlanInfo,
        modalTitle,
        modalState,
        editId,
        fieldValues,
        confirmOpen,
        confirmItem,
        confirmType,
        careCircleConfirmOpen,
        pendingCareCircleItem,
        pendingCareCircleSection,
    } = useSelector((state: RootState) => state.safetyPlanSlice);
    const authData = new Authorization();
    const safetyPlanNeeded = useSelector((state: RootState) => state.memberDemographicsDetails.chartDetails?.SafetyPlanNeeded);

    const [mostImportant, setMostImportant] = useState(safetyPlanInfo?.MostImportant || '');
    const [showDeclineReason, setShowDeclineReason] = useState(false);
    const [declineReason, setDeclineReason] = useState('');
    const [showConfirm, setShowConfirm] = useState(false);

    const hasFilledSafetyPlanContent =
        safetyPlanInfo?.Status === PlanStatusEnum.Created ||
        Boolean(safetyPlanInfo?.MostImportant?.trim()) ||
        SECTION_CONFIG.some((section) => Array.isArray(safetyPlanInfo?.[section.key]) && safetyPlanInfo[section.key].length > 0);

    // Function to check if all of the keys in the SECTION_CONFIG have values in safety plan info
    const isSafetyPlanComplete = () => {
        return SECTION_CONFIG.every((section) => {
            if (Array.isArray(safetyPlanInfo[section.key])) {
                return safetyPlanInfo[section.key].length > 0;
            }
            return Boolean(safetyPlanInfo[section.key]);
        });
    };

    useEffect(() => {
        if (memberId) dispatch(fetchSafetyPlanByMemberId(memberId));
    }, [dispatch, memberId]);

    useEffect(() => {
        if (safetyPlanInfo?.MostImportant) {
            setMostImportant(safetyPlanInfo.MostImportant || '');
        }
    }, [safetyPlanInfo?.MostImportant, dispatch]);

    const handleSaveDocument = () => {
        dispatch(savePlanPDFToDocuments(safetyPlanInfo.Id));
        setShowConfirm(false);
    };

    const handlePlanPromptAccept = () => {
        const safetyPlanCopy = copyObject(safetyPlanInfo);
        safetyPlanCopy.CarePlanType = CarePlanTypeEnum.SafetyPlan;
        safetyPlanCopy.Status = PlanStatusEnum.Created;
        safetyPlanCopy.MemberId = memberId;
        dispatch(saveSafetyPlan(safetyPlanCopy));
    };
    const handleShowDeclineReason = () => {
        setShowDeclineReason(true);
    };
    const handleCloseDeclineReason = () => {
        setShowDeclineReason(false);
        setDeclineReason('');
    };
    const handlePlanPromptDecline = () => {
        const safetyPlanCopy = copyObject(safetyPlanInfo);
        safetyPlanCopy.CarePlanType = CarePlanTypeEnum.SafetyPlan;
        safetyPlanCopy.Status = PlanStatusEnum.Declined;
        safetyPlanCopy.DeclinedReason = declineReason;
        safetyPlanCopy.MemberId = memberId;
        safetyPlanCopy.DeclinedBy = {
            Id: authData.UserId,
            Name: authData.UserName,
        };
        dispatch(saveSafetyPlan(safetyPlanCopy));
        handleCloseDeclineReason();
    };

    const handleAdd = (section) => {
        dispatch(setEditId({ ...editId, [section.key]: null }));
        dispatch(setFieldValues({ ...fieldValues, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setModalState({ ...modalState, [section.key]: true }));
        dispatch(setModalTitle(`Add ${section.sectionLabel}`));
    };
    const handleEdit = (section, row) => {
        dispatch(setEditId({ ...editId, [section.key]: row.Id }));
        const values = {};
        section.fields.forEach((f) => {
            values[f.name] = row[f.name] || '';
        });
        dispatch(setFieldValues({ ...fieldValues, [section.key]: values }));
        dispatch(setModalState({ ...modalState, [section.key]: true }));
        dispatch(setModalTitle(`Edit ${section.sectionLabel}`));
    };
    const handleSubmit = (section) => {
        const values = fieldValues[section.key];
        if (section.fields.some((f) => !String(values[f.name] ?? '').trim())) {
            setShowMessage(true, `Please enter all required fields.`, 'error');
            return;
        }
        if (!editId[section.key] && values.Type === '1') {
            dispatch(setPendingCareCircleItem({ ...values }));
            dispatch(setPendingCareCircleSection(section));
            dispatch(setCareCircleConfirmOpen(true));
            return;
        }
        if (editId[section.key]) {
            const itemsCopy = copyObject(safetyPlanInfo[section.key]);
            const existing = itemsCopy.find((item) => item.Id === editId[section.key]);
            section.fields.forEach((f) => {
                existing[f.name] = values[f.name];
            });
            const safetyPlanCopy = copyObject(safetyPlanInfo);
            safetyPlanCopy[section.key] = itemsCopy;
            dispatch(saveSafetyPlan(safetyPlanCopy));
        } else {
            const newItem = new PlanItem();
            section.fields.forEach((f) => {
                newItem[f.name] = values[f.name];
            });
            const safetyPlanCopy = copyObject(safetyPlanInfo);
            safetyPlanCopy[section.key] = [...safetyPlanCopy[section.key], newItem];
            dispatch(saveSafetyPlan(safetyPlanCopy));
        }
        dispatch(setFieldValues({ ...fieldValues, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setEditId({ ...editId, [section.key]: null }));
        dispatch(setModalState({ ...modalState, [section.key]: false }));
    };

    const handleCareCircleConfirm = (addToCareCircle) => {
        if (addToCareCircle) {
            const careCircleItem = getDefaultCareCircle();
            careCircleItem.Name = pendingCareCircleItem.Name;
            careCircleItem.HomePhone = pendingCareCircleItem.Phone.replace(/[^\d.]/g, '');
            dispatch(saveCareCircleItem(careCircleItem, memberId));
        }
        const section = pendingCareCircleSection;
        const newItem = new PlanItem();
        newItem.IsCareCircle = true;
        section.fields.forEach((f) => {
            newItem[f.name] = pendingCareCircleItem[f.name];
        });
        const safetyPlanCopy = copyObject(safetyPlanInfo);
        safetyPlanCopy[section.key] = [...safetyPlanCopy[section.key], newItem];
        dispatch(saveSafetyPlan(safetyPlanCopy));
        dispatch(setFieldValues({ ...fieldValues, [section.key]: getInitialFieldValues(section.fields) }));
        dispatch(setEditId({ ...editId, [section.key]: null }));
        dispatch(setModalState({ ...modalState, [section.key]: false }));
        dispatch(setCareCircleConfirmOpen(false));
        dispatch(setPendingCareCircleItem(null));
        dispatch(setPendingCareCircleSection(null));
    };

    const handleDelete = (row, type) => {
        openConfirm(row, type);
    };

    const openConfirm = (item, type) => {
        dispatch(setConfirmItem(item));
        dispatch(setConfirmType(type));
        dispatch(setConfirmOpen(true));
    };
    const closeConfirm = () => {
        dispatch(setConfirmOpen(false));
        dispatch(setConfirmItem(null));
        dispatch(setConfirmType(''));
    };

    const handleConfirmDelete = () => {
        if (!confirmItem) return;
        const itemsCopy = copyObject(safetyPlanInfo[confirmType]);
        const index = itemsCopy.findIndex((item) => item.Id === confirmItem.Id);
        if (index === -1) {
            console.error('Item not found in section:', confirmType);
            closeConfirm();
            return;
        }
        itemsCopy.splice(index, 1);
        const safetyPlanCopy = copyObject(safetyPlanInfo);
        safetyPlanCopy[confirmType] = itemsCopy;
        dispatch(saveSafetyPlan(safetyPlanCopy));
        closeConfirm();
    };

    return (
        <Box sx={{ padding: 2, minHeight: '80vh', pb: 5 }}>
            <Dialog open={showDeclineReason} disableEscapeKeyDown maxWidth="md" fullWidth onClose={() => setShowDeclineReason(false)}>
                <DialogTitle>Decline Safety Plan?</DialogTitle>
                <DialogContent>
                    <TextField
                        autoFocus
                        margin="dense"
                        id="decline-reason"
                        label="Reason for Declining"
                        type="text"
                        multiline
                        rows={4}
                        required
                        fullWidth
                        variant="outlined"
                        value={declineReason}
                        onChange={(e) => setDeclineReason(e.target.value)}
                    />
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDeclineReason} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        Cancel
                    </Button>
                    <Button disabled={!declineReason} onClick={handlePlanPromptDecline} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Submit
                    </Button>
                </DialogActions>
            </Dialog>
            {safetyPlanInfo?.Status !== PlanStatusEnum.Declined && !hasFilledSafetyPlanContent && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        {safetyPlanNeeded ? (
                            <span>
                                <b style={{ color: 'red' }}>A Safety Plan is recommended for this member.</b> Would you like to create one now?
                            </span>
                        ) : (
                            'No Safety Plan found for this member. Would you like to create a Safety Plan now?'
                        )}
                    </Typography>
                    <Stack direction="row" spacing={2} mt={2}>
                        <Button variant="outlined" sx={{ minWidth: '120px' }} color="primary" onClick={handleShowDeclineReason}>
                            Decline
                        </Button>
                        <Button variant="contained" sx={{ minWidth: '120px' }} color="primary" onClick={handlePlanPromptAccept}>
                            Create Safety Plan
                        </Button>
                    </Stack>
                </Paper>
            )}
            {safetyPlanInfo?.Status === PlanStatusEnum.Declined && (
                <Paper sx={{ p: 3, mb: 3 }}>
                    <Typography variant="body1" color="text.secondary">
                        Safety Plan creation was declined by <b>{safetyPlanInfo?.DeclinedBy?.Name}</b> on{' '}
                        <b>
                            {(() => {
                                const d = toDate(safetyPlanInfo?.DeclinedAt);
                                return d ? formatDate(d, 'MM/dd/yyyy') : '';
                            })()}
                        </b>
                        .{' '}
                    </Typography>
                    <Typography variant="body1" color="text.secondary" sx={{ mt: 1 }}>
                        Reason for Decline: <b>{safetyPlanInfo.DeclinedReason || ' - '}</b>
                    </Typography>
                    <Button sx={{ mt: 2 }} variant="contained" color="primary" onClick={handlePlanPromptAccept}>
                        Create Safety Plan
                    </Button>
                </Paper>
            )}
            {safetyPlanInfo?.Status !== PlanStatusEnum.Declined && hasFilledSafetyPlanContent && (
                <>
                    <Stack direction="row" alignItems="center" spacing={1} mb={2}>
                        <Typography variant="h5" fontWeight="bold">
                            Safety Plan
                        </Typography>
                        <Tooltip title="Safety Plan help page.">
                            <InfoIcon color="primary" />
                        </Tooltip>
                        <Box sx={{ flex: 1 }} />
                        {Boolean(safetyPlanInfo?.UpdatedBy) ? (
                            <Chip
                                size="small"
                                label={`Last Reviewed: ${safetyPlanInfo.UpdatedBy?.Name} - ${
                                    safetyPlanInfo?.UpdatedAt
                                        ? (() => {
                                              const d = toDate(safetyPlanInfo?.UpdatedAt);
                                              return d ? formatDate(d, 'MM/dd/yyyy') : '';
                                          })()
                                        : ''
                                }`}
                            />
                        ) : (
                            <Chip size="small" label={`Last Reviewed: -`} />
                        )}
                    </Stack>
                    <Divider sx={{ mb: 3 }} />
                    <Grid container spacing={2}>
                        {SECTION_CONFIG.map((section) => (
                            <Section
                                key={section.key}
                                sectionLabel={section.sectionLabel}
                                title={section.title}
                                items={safetyPlanInfo[section.key]}
                                onAdd={() => handleAdd(section)}
                                addLabel={section.addLabel}
                                renderItem={(row, idx) => (
                                    <ItemCard
                                        key={row.Id || idx}
                                        note={section.fields.length === 1 ? row.Note : ''}
                                        createdAt={row.CreatedAt}
                                        createdBy={row.CreatedBy}
                                        updatedAt={row.UpdatedAt}
                                        updatedBy={row.UpdatedBy}
                                        onEdit={() => handleEdit(section, row)}
                                        onDelete={() => handleDelete(row, section.key)}
                                        extraFields={
                                            section.fields.length > 1 && (
                                                <>
                                                    {row.Name && <Typography variant="body2">Name: {row.Name}</Typography>}
                                                    {row.Phone && <Typography variant="body2">Phone: {row.Phone}</Typography>}
                                                    {row.Note && <Typography variant="body2">Note: {row.Note}</Typography>}
                                                </>
                                            )
                                        }
                                    />
                                )}
                            />
                        ))}
                        <Grid size={12}>
                            <Paper elevation={2} sx={{ p: 2 }}>
                                <Typography variant="h6" fontWeight="bold" color="primary" sx={{ mb: 1 }}>
                                    Most Important
                                </Typography>
                                <Typography fontWeight={'bold'} variant="body1" sx={{ mb: 1 }}>
                                    The one thing that is most important to me and worth living for is:
                                </Typography>
                                <TextField
                                    sx={{ width: { xs: '100%', sm: '75%' } }}
                                    multiline
                                    rows={3}
                                    variant="outlined"
                                    value={mostImportant}
                                    onChange={(e) => {
                                        setMostImportant(e.target.value);
                                    }}
                                    placeholder="Enter the most important thing to remember"
                                />
                                <Button
                                    variant="contained"
                                    color="primary"
                                    sx={{ mt: 2, display: 'block', width: { xs: '100%', sm: 'auto' } }}
                                    onClick={() => dispatch(saveSafetyPlan({ ...safetyPlanInfo, MostImportant: mostImportant }))}
                                    disabled={!mostImportant.trim() || mostImportant === safetyPlanInfo?.MostImportant}
                                >
                                    Save Most Important
                                </Button>
                            </Paper>
                        </Grid>
                    </Grid>
                    {SECTION_CONFIG.map((section) => (
                        <AddPlanModal
                            key={section.key}
                            open={!!modalState[section.key]}
                            onClose={() => {
                                dispatch(setModalState({ ...modalState, [section.key]: false }));
                                dispatch(setEditId({ ...editId, [section.key]: null }));
                                dispatch(setFieldValues({ ...fieldValues, [section.key]: getInitialFieldValues(section.fields) }));
                            }}
                            title={modalTitle}
                            onSubmit={() => handleSubmit(section)}
                            isEdit={!!editId[section.key]}
                        >
                            <Stack spacing={2} pt={2}>
                                {section.fields.map((f) =>
                                    f.name === 'Type' ? (
                                        <FormControl component="fieldset" fullWidth key={f.name} sx={{ mt: 1 }}>
                                            <FormLabel component="legend">{f.label}</FormLabel>
                                            <RadioGroup
                                                row
                                                name={`type-radio-${section.key}`}
                                                value={fieldValues[section.key]?.[f.name] || ''}
                                                onChange={(e) => {
                                                    let value = e.target.value;
                                                    dispatch(
                                                        setFieldValues({
                                                            ...fieldValues,
                                                            [section.key]: { ...fieldValues[section.key], [f.name]: value },
                                                        })
                                                    );
                                                }}
                                            >
                                                <FormControlLabel value={PlanItemTypeEnum.Person} control={<Radio />} label="Person" />
                                                <FormControlLabel value={PlanItemTypeEnum.Place} control={<Radio />} label="Place" />
                                            </RadioGroup>
                                        </FormControl>
                                    ) : f.mask ? (
                                        <FormControl fullWidth key={f.name}>
                                            <InputLabel htmlFor={`formatted-text-mask-input-${section.key}`}>{f.label}</InputLabel>
                                            <OutlinedInput
                                                value={fieldValues[section.key]?.[f.name] || ''}
                                                onChange={(e) =>
                                                    dispatch(
                                                        setFieldValues({
                                                            ...fieldValues,
                                                            [section.key]: { ...fieldValues[section.key], [f.name]: e.target.value },
                                                        })
                                                    )
                                                }
                                                name={f.name}
                                                required
                                                label={f.label}
                                                id={`formatted-text-mask-input-${section.key}`}
                                                inputComponent={TextMaskCustom as any}
                                            />
                                        </FormControl>
                                    ) : (
                                        <TextField
                                            key={f.name}
                                            autoFocus={f.name === section.fields[0].name}
                                            margin="dense"
                                            id={f.name}
                                            label={f.label}
                                            multiline={!!f.multiline}
                                            rows={f.multiline ? 3 : 1}
                                            type="text"
                                            fullWidth
                                            variant="outlined"
                                            value={fieldValues[section.key]?.[f.name] || ''}
                                            onChange={(e) =>
                                                dispatch(
                                                    setFieldValues({ ...fieldValues, [section.key]: { ...fieldValues[section.key], [f.name]: e.target.value } })
                                                )
                                            }
                                        />
                                    )
                                )}
                            </Stack>
                        </AddPlanModal>
                    ))}
                </>
            )}
            <Dialog open={confirmOpen} onClose={closeConfirm}>
                <DialogTitle>Confirm Delete</DialogTitle>
                <DialogContent>
                    <DialogContentText>Do you wish to delete this item from {confirmType}?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={closeConfirm} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        Cancel
                    </Button>
                    <Button onClick={handleConfirmDelete} color="error" variant="contained" sx={{ minWidth: '120px' }}>
                        Delete
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Care Circle Confirmation Dialog */}
            <Dialog open={careCircleConfirmOpen} onClose={() => handleCareCircleConfirm(false)}>
                <DialogTitle>Add to Care Circle?</DialogTitle>
                <DialogContent>
                    <DialogContentText>Would you like to add this person to the member's Care Circle?</DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => handleCareCircleConfirm(false)} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        No
                    </Button>
                    <Button onClick={() => handleCareCircleConfirm(true)} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {/* Save PDF to Documents Confirmation Dialog */}
            <Dialog open={showConfirm} onClose={() => setShowConfirm(false)}>
                <DialogTitle>Save PDF to Documents?</DialogTitle>
                <DialogContent>
                    <DialogContentText>
                        <Typography variant="body1">Would you like to save the PDF to the Documents folder?</Typography>
                        {!isSafetyPlanComplete() && (
                            <Typography variant="body1" color="error">
                                {' '}
                                Not all fields are complete.
                            </Typography>
                        )}
                    </DialogContentText>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setShowConfirm(false)} color="primary" variant="outlined" sx={{ minWidth: '120px' }}>
                        No
                    </Button>
                    <Button onClick={handleSaveDocument} color="primary" variant="contained" sx={{ minWidth: '120px' }}>
                        Yes
                    </Button>
                </DialogActions>
            </Dialog>
            {hasFilledSafetyPlanContent && (
                <Grid size={12} sx={{ textAlign: 'center', mt: 4 }}>
                    <Button
                        variant="contained"
                        color="success"
                        size="small"
                        sx={{
                            boxShadow: 4,
                            borderRadius: 8,
                            px: 2,
                            py: 1,
                            fontWeight: 'bold',
                        }}
                        onClick={() => setShowConfirm(true)}
                    >
                        Save PDF to Documents
                    </Button>
                </Grid>
            )}
        </Box>
    );
};

export default SafetyPlan;
